import { useEffect, useState } from 'react';
import Select from 'react-select';
import toast from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import PatientVerificationForm from '~/components/PatientVerificationForm';
import { downloadMedicalVerificationPdf } from '~/utils/medicalVerificationPdf';
import { apiUrl, authHeaders } from '~/services/auth';
import { inputStyles, labelStyles } from './styles';

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    patient: any;
    application?: any;
    campaign?: any;
    campaignId?: number;
    metadata?: any;
    onUpdate?: () => void;
}

// mp_certify_identity values -> who gave consent (mirrors medicalVerificationPdf.ts)
const IDENTITY_LABELS: Record<number, string> = {
    1: 'Client',
    2: 'Spouse',
    3: 'Parent of minor',
    4: 'Legal Guardian',
    5: 'POA',
};

// Resolve a medical_reason reference (object or id) to its id.
const reasonIdOf = (medicalReason: any): number | null => {
    if (!medicalReason) return null;
    return typeof medicalReason === 'object' ? medicalReason.id : medicalReason;
};

// CS-facing manual verification (HHLTB-2554). Opens from the Medical Info tab's
// "Verify Now" button so staff can verify a patient's medical need in place
// instead of dropping into legacy admin. Diagnosis/Injury, date of diagnosis,
// and additional medical info are editable (prepopulated from the application)
// and saved through the existing medicalInfo endpoint as part of the Verify
// action; consent is shown read-only with a download.
const PatientVerificationDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    patient,
    application,
    campaign,
    campaignId,
    metadata,
    onUpdate,
}) => {
    const [primaryMedicalIssue, setPrimaryMedicalIssue] = useState<any>(null);
    const [secondaryMedicalIssue, setSecondaryMedicalIssue] =
        useState<any>(null);
    const [medicalIssueDate, setMedicalIssueDate] = useState('');
    const [notes, setNotes] = useState('');

    // Prepopulate the editable fields from the patient each time the drawer opens.
    useEffect(() => {
        if (!isOpen) return;
        const pmi = patient?.primary_medical_issue;
        const smi = patient?.secondary_medical_issue;
        setPrimaryMedicalIssue(
            pmi ? { value: pmi.id, label: pmi.name } : null
        );
        setSecondaryMedicalIssue(
            smi ? { value: smi.id, label: smi.name } : null
        );
        setMedicalIssueDate(patient?.medical_issue_date || '');
        setNotes(patient?.notes || '');
    }, [isOpen, patient]);

    // Keep the reason fixed: only offer issues under the patient's current
    // primary medical reason, so a manual verification can correct the specific
    // diagnosis without reclassifying the campaign's fund. Changing the reason
    // itself stays in the dedicated "Edit" drawer.
    const currentReasonId = reasonIdOf(patient?.primary_medical_issue?.medical_reason);
    const issueOptions = (metadata?.medical_issues || [])
        .filter((mi: any) => reasonIdOf(mi.medical_reason) === currentReasonId)
        .map((mi: any) => ({ value: mi.id, label: mi.name }));

    const consentName = (application?.mp_signature ?? '').toString().trim();
    const relationship =
        IDENTITY_LABELS[Number(application?.mp_certify_identity)] ?? '';
    const hasConsent = !!consentName || !!relationship;

    const handleDownloadConsent = async () => {
        try {
            await downloadMedicalVerificationPdf(campaign, patient, application);
        } catch (e) {
            console.error('Failed to download consent form:', e);
            toast.error('Failed to download consent form');
        }
    };

    // Save the medical-info edits through the same endpoint the Edit button uses.
    // Runs just before the verification is recorded (via onBeforeVerify). The
    // endpoint applies only the fields sent, so transplant/timeframe fields are
    // untouched.
    const saveMedicalInfo = async () => {
        if (!campaignId) return;
        const resp = await fetch(`${apiUrl}/admin/campaigns/${campaignId}/medicalInfo`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({
                primary_medical_issue_id: primaryMedicalIssue?.value ?? null,
                secondary_medical_issue_id: secondaryMedicalIssue?.value ?? null,
                medical_issue_date: medicalIssueDate || null,
                notes,
            }),
        });
        if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            throw new Error(
                body.error || body.detail || 'Failed to save medical information'
            );
        }
    };

    const handleVerified = () => {
        onUpdate?.();
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Patient Verification'>
            <h3 className='mb-4 text-[16px] font-bold uppercase text-[#949599]'>
                Medical Expenses for Verification
            </h3>

            {hasConsent && (
                <div className='mb-6'>
                    <label className='text-[12px]/[16px] text-[#949599]'>
                        Client Consent
                    </label>
                    <div className='font-bold'>
                        {consentName ? `Given by ${consentName}` : 'Given'}
                        {relationship ? `, ${relationship}` : ''}
                    </div>
                    {application && (
                        <button
                            type='button'
                            onClick={handleDownloadConsent}
                            className='mt-1 inline-flex items-center gap-1 text-[14px] text-primary hover:opacity-80'
                        >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='16'
                                height='16'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='2'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            >
                                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                                <polyline points='7 10 12 15 17 10' />
                                <line x1='12' y1='15' x2='12' y2='3' />
                            </svg>
                            Download Consent Form
                        </button>
                    )}
                </div>
            )}

            <div className='mb-6'>
                <label className={labelStyles}>Diagnosis/Injury</label>
                <Select
                    menuPlacement='auto'
                    value={primaryMedicalIssue}
                    onChange={setPrimaryMedicalIssue}
                    options={issueOptions}
                    isClearable
                    placeholder='Select Diagnosis/Injury'
                    className='react-select-container'
                    classNamePrefix='react-select'
                />
            </div>

            <div className='mb-6'>
                <label className={labelStyles}>
                    Secondary Diagnosis/Injury
                </label>
                <Select
                    menuPlacement='auto'
                    value={secondaryMedicalIssue}
                    onChange={setSecondaryMedicalIssue}
                    options={issueOptions}
                    isClearable
                    placeholder='Select Secondary Diagnosis/Injury'
                    className='react-select-container'
                    classNamePrefix='react-select'
                />
            </div>

            <div className='mb-6'>
                <label className={labelStyles}>Date of Diagnosis/Injury</label>
                <input
                    type='date'
                    value={medicalIssueDate}
                    onChange={(e) => setMedicalIssueDate(e.target.value)}
                    className={inputStyles + ' w-full'}
                />
            </div>

            <div className='mb-6'>
                <label className={labelStyles}>
                    Additional Medical Information (if applicable)
                </label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className={inputStyles + ' w-full'}
                    placeholder='Text'
                />
            </div>

            <hr className='my-6' />

            <PatientVerificationForm
                patient={patient}
                onBeforeVerify={saveMedicalInfo}
                onVerified={handleVerified}
                onCancel={() => setIsOpen(false)}
            />
        </Drawer>
    );
};

export default PatientVerificationDrawer;
